import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLeaves, fetchLeaveReasons, applyLeave, deleteLeave } from '@/store/slices/leaveSlice';
import Toaster from '@/components/Toaster';
import ShowLoader from '@/components/Loader';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const DAYS_OF_WEEK = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => currentYear + i);

export default function LeaveManagement() {
  const { t } = useTranslation('leave');
  const dispatch = useDispatch();
  const currentActualDate = new Date();

  // Redux State
  const { appliedLeavesDates, appliedLeaves, leaveReasons, isApplying } = useSelector((state) => state.leave);

  // Calendar State (Default to current month/year)
  const [displayMonth, setDisplayMonth] = useState(currentActualDate.getMonth());
  const [displayYear, setDisplayYear] = useState(currentActualDate.getFullYear());

  // Form State & Range Selection
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [leaveReasonId, setLeaveReasonId] = useState('');
  const [description, setDescription] = useState('');
  const [selectedLeave, setSelectedLeave] = useState(null);

  // New state for View All modal
  const [showAllModal, setShowAllModal] = useState(false);

  // Helper to calculate leave duration in days (inclusive)
  const getDifferenceInDays = (start, end) => {
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const [errors, setErrors] = useState({});

  // Fetch initial data on mount
  useEffect(() => {
    dispatch(fetchLeaves());
    dispatch(fetchLeaveReasons());
  }, [dispatch]);

  // Helper Functions
  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => {
    let day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  // Format to API requirement: YYYY-MM-DD
  const formatDateString = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Calendar Interactions
  const handleDayClick = (day) => {
    const clickedDate = new Date(displayYear, displayMonth, day);

    // Prevent clicking past dates OR already applied dates
    if (clickedDate < today || isDateAlreadyApplied(clickedDate)) {
      if (isDateAlreadyApplied(clickedDate)) {
        Toaster.error(t("alreadyAppliedError"));
      }
      return;
    }

    if (!fromDate || (fromDate && toDate)) {
      setFromDate(clickedDate);
      setToDate(null);
      setHoverDate(null);
    } else {
      // Check if the range between fromDate and clickedDate contains an already applied date
      if (isRangeOverlapping(fromDate, clickedDate)) {
        Toaster.error(t("rangeOverlapError"));
        return;
      }

      if (clickedDate < fromDate) {
        setToDate(fromDate);
        setFromDate(clickedDate);
      } else {
        setToDate(clickedDate);
      }
      setHoverDate(null);
    }
  };

  const handleMouseEnter = (day) => {
    const hovered = new Date(displayYear, displayMonth, day);
    if (hovered < today) return;

    if (fromDate && !toDate) {
      setHoverDate(hovered);
    }
  };

  const handleMouseLeaveGrid = () => {
    if (fromDate && !toDate) {
      setHoverDate(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!leaveReasonId) {
      newErrors.leaveReasonId = t("selectReasonError");
    }

    const comment = description.trim();

    if (!comment) {
      newErrors.description = t("enterCommentError");
    } else if (comment.length < 10) {
      newErrors.description = t("commentMinLengthError");
    } else if (comment.length > 250) {
      newErrors.description = t("commentMaxLengthError");
    } else if (!/^[A-Za-z0-9][A-Za-z0-9 ]*$/.test(comment)) {
      newErrors.description =
        t("commentValidationError");
    }
    const finalToDate = toDate || fromDate;
    if (fromDate && finalToDate && isRangeOverlapping(fromDate, finalToDate)) {
      newErrors.fromDate = t("rangeContainsAppliedError");
      Toaster.error(t("rangeContainsAppliedError"));
    } else if (fromDate && finalToDate) {
      const maxDaysAllowed = 30; // Adjust this limit based on company policy
      const leaveDuration = getDifferenceInDays(fromDate, finalToDate);

      if (leaveDuration > maxDaysAllowed) {
        newErrors.fromDate = t("maxDaysError", { maxDaysAllowed });
        Toaster.error(t("maxDurationError", { maxDaysAllowed }));
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {

    if (!validateForm()) {
      return;
    }
    // Determine the actual toDate (if user only selected one day, fromDate and toDate are the same)
    const finalToDate = toDate || fromDate;

    const payload = {
      leave_reason_id: Number(leaveReasonId),
      leave_from: formatDateString(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate()),
      leave_to: formatDateString(finalToDate.getFullYear(), finalToDate.getMonth(), finalToDate.getDate()),
      description: description
    };

    const resultAction = await dispatch(applyLeave(payload));

    if (applyLeave.fulfilled.match(resultAction)) {
      Toaster.success(t("applySuccess"));
      // Reset form
      setFromDate(null);
      setToDate(null);
      setHoverDate(null);
      setLeaveReasonId('');
      setDescription('');
    } else {
      Toaster.error(t("applyFailed", { message: resultAction.payload?.message || t("somethingWentWrong") }));
    }
  };

  const daysInMonth = getDaysInMonth(displayMonth, displayYear);
  const firstDay = getFirstDayOfMonth(displayMonth, displayYear);
  const emptySlots = Array.from({ length: firstDay }, (_, i) => i);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Handle dropdown date changes for From/To dates
  const handleDateDropdownChange = (target, type, value) => {
    const baseDate = target === 'from'
      ? (fromDate || new Date(displayYear, displayMonth, today.getDate()))
      : (toDate || fromDate || new Date(displayYear, displayMonth, today.getDate()));

    let year = baseDate.getFullYear();
    let month = baseDate.getMonth();
    let day = baseDate.getDate();

    if (type === 'day') day = Number(value);
    if (type === 'month') month = Number(value);
    if (type === 'year') year = Number(value);

    const maxDays = getDaysInMonth(month, year);
    if (day > maxDays) day = maxDays;

    let newDate = new Date(year, month, day);

    // Prevent past date selection by snapping to today if a past date is formed
    if (newDate < today) {
      newDate = new Date(today);
    }

    if (target === 'from') {
      setFromDate(newDate);
      if (toDate && newDate > toDate) {
        setToDate(newDate);
      }
    } else {
      if (fromDate && newDate < fromDate) {
        setFromDate(newDate);
        setToDate(fromDate);
      } else {
        setToDate(newDate);
      }
    }
  };

  // Filter out past months if the current year is selected
  const getAvailableMonths = (selectedYear) => {
    const yearToCheck = selectedYear || today.getFullYear();
    // return MONTHS.map((m, idx) => ({ value: idx, label: m }))
    return MONTHS.map((m, idx) => ({ value: idx, label: t(`months.${m}`) }))
      .filter(opt => yearToCheck > today.getFullYear() || opt.value >= today.getMonth());
  };

  // Filter out past days if the current month and year are selected
  const getAvailableDays = (selectedMonth, selectedYear) => {
    const yearToCheck = selectedYear || today.getFullYear();
    const monthToCheck = selectedMonth !== undefined && selectedMonth !== null ? selectedMonth : today.getMonth();
    const maxDays = getDaysInMonth(monthToCheck, yearToCheck);
    const startDay = (yearToCheck === today.getFullYear() && monthToCheck === today.getMonth()) ? today.getDate() : 1;

    return Array.from({ length: maxDays - startDay + 1 }, (_, i) => ({
      value: startDay + i,
      label: startDay + i
    }));
  };

  // Check if a specific Date object is already in the appliedLeavesDates array
  const isDateAlreadyApplied = (dateObj) => {
    if (!dateObj) return false;
    const dateStr = formatDateString(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
    return appliedLeavesDates.includes(dateStr);
  };

  // Check if any date between start and end is already applied
  const isRangeOverlapping = (startObj, endObj) => {
    if (!startObj || !endObj) return false;
    const start = new Date(Math.min(startObj, endObj));
    const end = new Date(Math.max(startObj, endObj));

    let current = new Date(start);
    while (current <= end) {
      if (isDateAlreadyApplied(current)) return true;
      current.setDate(current.getDate() + 1);
    }
    return false;
  };

  // Open modal with leave details when an applied date is clicked
  const handleAppliedDateClick = (dateStr) => {
    // Find the leave record that contains this date
    const foundLeave = appliedLeaves.find(leave => {
      const from = new Date(leave.leave_from).setHours(0, 0, 0, 0);
      const to = new Date(leave.leave_to).setHours(0, 0, 0, 0);
      const target = new Date(dateStr).setHours(0, 0, 0, 0);
      return target >= from && target <= to;
    });

    if (foundLeave) {
      setSelectedLeave(foundLeave);
    } else {
      Toaster.error(t("loadDetailsError"));
    }
  };

  // Delete future leave
  const handleDeleteLeave = async (leaveId) => {
    Swal.fire({
      title: t("deleteConfirmTitle"),
      text: t("deleteConfirmText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("deleteConfirmButton"),
      cancelButtonText: t("cancelButton"),
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteLeave({ leave_id: leaveId })).unwrap().then((resultAction) => {
          console.log(resultAction);
          if (resultAction.status) {
            Toaster.success(t("deleteSuccess"));
            setSelectedLeave(null);
            dispatch(fetchLeaves()); // Refresh list
          } else {
            Toaster.error(t("deleteFailed", { message: resultAction.payload?.message || t("errorOccurred") }));
          }
        });
      }
    });
  };

  // Reusable Dropdown
  const DateSelect = ({ value, onChange, options, placeholder }) => (
    <div className="relative border rounded flex items-center bg-white flex-1 min-w-[70px] sm:min-w-[80px]">
      <select
        className="appearance-none w-full py-1.5 pl-2 sm:pl-3 pr-6 sm:pr-7 text-gray-700 bg-transparent outline-none text-xs sm:text-sm truncate cursor-pointer"
        value={value || ''}
        onChange={onChange}
      >
        <option value="" disabled>{t(placeholder)}</option>
        {options.map((opt, idx) => (
          <option key={idx} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <div className="absolute right-1.5 sm:right-2 pointer-events-none text-gray-500">
        <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-8 font-sans">
      {isApplying && <ShowLoader />}
      <div className="max-w-6xl mx-auto">

        <div className="flex items-center gap-2 mb-6">
          <h1 className="text-xl font-semibold text-gray-900">{t("leaveManagementTitle")}</h1>
          <button
            className="text-gray-500 hover:text-gray-700 transition-colors"
            onClick={() => { setFromDate(null); setToDate(null); setHoverDate(null); }}
            title={t("resetDatesTitle")}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
          </button>
        </div>

        <p className="text-gray-600 mb-4">{t("chooseDate")}</p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* LEFT: Calendar Panel */}
          <div className="lg:col-span-5 bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="flex justify-center items-center gap-4 py-6 border-b border-gray-100">
              <div className="relative">
                <select
                  className="appearance-none font-medium text-green-800 outline-none pr-6 cursor-pointer bg-transparent"
                  value={displayMonth}
                  onChange={(e) => setDisplayMonth(Number(e.target.value))}
                >
                  {MONTHS.map((m, i) => {
                    const isCurrentYear = displayYear === currentActualDate.getFullYear();

                    if (isCurrentYear && i < currentActualDate.getMonth()) {
                      return null;
                    }

                    return (
                      <option key={i} value={i}>
                        {/* {m} */}
                        {t(`months.${m}`)}
                      </option>
                    );
                  })}
                </select>
                <svg className="w-4 h-4 absolute right-0 top-1 text-green-800 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
              </div>
              {/* <div className="relative">
                <select 
                  className="appearance-none font-medium text-gray-800 outline-none cursor-pointer bg-transparent"
                  value={displayYear}
                  onChange={(e) => setDisplayYear(Number(e.target.value))}
                >
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div> */}
              <div className="relative">
                <select
                  className="appearance-none font-medium text-gray-800 outline-none pr-6 cursor-pointer bg-transparent"
                  value={displayYear}
                  onChange={(e) => {
                    const year = Number(e.target.value);

                    setDisplayYear(year);

                    if (
                      year === currentActualDate.getFullYear() &&
                      displayMonth < currentActualDate.getMonth()
                    ) {
                      setDisplayMonth(currentActualDate.getMonth());
                    }
                  }}
                >
                  {YEARS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>

                <svg
                  className="w-4 h-4 absolute right-0 top-1 text-gray-800 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            <div className="p-6" onMouseLeave={handleMouseLeaveGrid}>
              <div className="grid grid-cols-7 gap-y-4 text-center mb-2">
                {DAYS_OF_WEEK.map(day => (
                  <div key={day} className="text-gray-600 font-medium text-sm">{day}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-y-2 text-center">
                {emptySlots.map(slot => (
                  <div key={`empty-${slot}`}></div>
                ))}

                {days.map(day => {
                  const dateString = formatDateString(displayYear, displayMonth, day);

                  // Check against Redux state data
                  const isApplied = appliedLeavesDates.includes(dateString);

                  const current = new Date(displayYear, displayMonth, day).getTime();
                  const start = fromDate?.getTime();
                  const end = toDate?.getTime();
                  const hover = hoverDate?.getTime();
                  const isPastDate = current < today.getTime();
                  const isDisabled = isPastDate || isApplied;

                  let isSelected = false;
                  let isStart = false;
                  let isEnd = false;
                  let inRange = false;

                  if (start && end) {
                    isSelected = current === start || current === end;
                    isStart = current === Math.min(start, end);
                    isEnd = current === Math.max(start, end);
                    inRange = current > Math.min(start, end) && current < Math.max(start, end);
                  } else if (start && !end && hover) {
                    isSelected = current === start || current === hover;
                    isStart = current === Math.min(start, hover);
                    isEnd = current === Math.max(start, hover);
                    inRange = current > Math.min(start, hover) && current < Math.max(start, hover);
                  } else if (start && !end) {
                    isSelected = current === start;
                    isStart = current === start;
                    isEnd = current === start;
                  }

                  let bgLayerClass = "";
                  if (inRange) {
                    bgLayerClass = "bg-green-50";
                  } else if (isStart && !isEnd) {
                    bgLayerClass = "bg-green-50 rounded-l-full";
                  } else if (isEnd && !isStart) {
                    bgLayerClass = "bg-green-50 rounded-r-full";
                  }

                  return (
                    <div
                      key={day}
                      className={`relative flex flex-col items-center justify-center h-10 w-full ${isPastDate && !isApplied ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${bgLayerClass}`}
                      onClick={() => {
                        if (isApplied) {
                          handleAppliedDateClick(dateString);
                        } else if (!isPastDate) {
                          handleDayClick(day);
                        }
                      }}
                      onMouseEnter={() => !isDisabled && handleMouseEnter(day)}
                    >
                      <div className={`relative z-10 w-8 h-8 flex items-center justify-center rounded-full text-sm transition-all
                        ${isPastDate ? 'text-gray-300' : isApplied ? 'text-gray-700' : isSelected ? 'bg-green-800 text-white font-medium shadow-sm' : 'text-gray-700'}
                        ${!isSelected && !inRange && !isDisabled ? 'hover:bg-gray-100' : ''}
                      `}>
                        {day}
                      </div>

                      <div className="absolute bottom-[2px] w-full flex justify-center z-10">
                        {isApplied && (
                          <div className="w-2 h-2 rounded-full bg-green-800"></div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT: Form Panel */}
          <div className="lg:col-span-7 flex flex-col gap-4">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden flex flex-col">

              {/* FROM DATE DROPDOWNS */}
              <div className="grid grid-cols-1 sm:grid-cols-12 border-b border-gray-200">
                <div className="p-3 sm:p-4 sm:col-span-4 lg:col-span-4 border-b sm:border-b-0 sm:border-r border-gray-200 text-gray-700 font-medium flex items-center text-sm">
                  {t("fromLabel")}
                </div>
                <div className="p-3 sm:p-4 sm:col-span-8 lg:col-span-8 flex flex-wrap sm:flex-nowrap gap-2 sm:gap-3 items-center">
                  <DateSelect
                    placeholder="day"
                    value={fromDate ? fromDate.getDate() : ''}
                    options={getAvailableDays(fromDate?.getMonth(), fromDate?.getFullYear())}
                    onChange={(e) => handleDateDropdownChange('from', 'day', e.target.value)}
                  />
                  <DateSelect
                    placeholder="month"
                    value={fromDate ? fromDate.getMonth() : ''}
                    options={getAvailableMonths(fromDate?.getFullYear())}
                    onChange={(e) => handleDateDropdownChange('from', 'month', e.target.value)}
                  />
                  <DateSelect
                    placeholder="year"
                    value={fromDate ? fromDate.getFullYear() : ''}
                    options={YEARS.map(y => ({ value: y, label: y }))}
                    onChange={(e) => handleDateDropdownChange('from', 'year', e.target.value)}
                  />
                </div>
              </div>

              {/* TO DATE DROPDOWNS */}
              <div className="grid grid-cols-1 sm:grid-cols-12 border-b border-gray-200">
                <div className="p-3 sm:p-4 sm:col-span-4 lg:col-span-4 border-b sm:border-b-0 sm:border-r border-gray-200 text-gray-700 font-medium flex items-center text-sm">
                  {t("toLabel")}
                </div>
                <div className="p-3 sm:p-4 sm:col-span-8 lg:col-span-8 flex flex-wrap sm:flex-nowrap gap-2 sm:gap-3 items-center">
                  <DateSelect
                    placeholder="day"
                    value={toDate ? toDate.getDate() : (fromDate && !hoverDate ? fromDate.getDate() : '')}
                    options={getAvailableDays(toDate?.getMonth() ?? fromDate?.getMonth(), toDate?.getFullYear() ?? fromDate?.getFullYear())}
                    onChange={(e) => handleDateDropdownChange('to', 'day', e.target.value)}
                  />
                  <DateSelect
                    placeholder="month"
                    value={toDate ? toDate.getMonth() : (fromDate && !hoverDate ? fromDate.getMonth() : '')}
                    options={getAvailableMonths(toDate?.getFullYear() ?? fromDate?.getFullYear())}
                    onChange={(e) => handleDateDropdownChange('to', 'month', e.target.value)}
                  />
                  <DateSelect
                    placeholder="year"
                    value={toDate ? toDate.getFullYear() : (fromDate && !hoverDate ? fromDate.getFullYear() : '')}
                    options={YEARS.map(y => ({ value: y, label: y }))}
                    onChange={(e) => handleDateDropdownChange('to', 'year', e.target.value)}
                  />
                </div>
                {errors.fromDate && <>
                  <div className="p-4 border-r border-gray-200 text-gray-700 flex items-center"></div>
                  <div className="p-4 col-span-2 flex gap-3 items-center">
                    <p className="text-red-500 text-xs mt-1">
                      {errors.fromDate}
                    </p>
                  </div>
                </>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-12 min-h-[200px]">
                <div className="p-3 sm:p-4 sm:col-span-4 lg:col-span-4 border-b sm:border-b-0 sm:border-r border-gray-200 text-gray-700 font-medium pt-3 sm:pt-5 text-sm">
                  {t("commentLabel")} <span className="text-red-500 ml-1">*</span>
                </div>
                <div className="p-3 sm:p-4 sm:col-span-8 lg:col-span-8 flex flex-col">
                  <div className="relative border rounded flex items-center bg-white max-w-sm">
                    <select
                      className="appearance-none w-full py-2 pl-3 pr-8 text-gray-700 bg-transparent outline-none text-sm cursor-pointer"
                      value={leaveReasonId}
                      onChange={(e) => setLeaveReasonId(e.target.value)}
                    >
                      <option value="" disabled>{t("selectPlaceholder")}</option>
                      {/* Populate reasons dynamically from Redux API response */}
                      {leaveReasons?.map((reason) => (
                        <option key={reason.id} value={reason.id}>
                          {/* {reason.leave_reason} */}
                          {t(`leaveReasons.${reason.leave_reason}`)}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 pointer-events-none text-gray-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                    </div>
                  </div>
                  {errors.leaveReasonId && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.leaveReasonId}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 min-h-[220px]">
                <div className="p-4 border-r border-gray-200 text-gray-700 pt-5">
                  {t("commentLabel")} <span className="text-red-500">*</span>
                </div>

                <div className="p-4 col-span-2 flex flex-col">
                  <textarea
                    className={`w-full flex-1 min-h-[120px] p-3 border rounded outline-none focus:border-green-800 transition-colors resize-none text-sm placeholder-gray-400 ${errors.description ? "border-red-500" : "border-gray-200"
                      }`}
                    placeholder={t("commentPlaceholder")}
                    maxLength={250}
                    value={description}
                    onChange={(e) => {
                      let value = e.target.value;

                      // Remove leading spaces
                      value = value.replace(/^\s+/, "");

                      // Allow only letters, numbers and spaces
                      value = value.replace(/[^A-Za-z0-9 ]/g, "");

                      setDescription(value);

                      if (errors.description) {
                        setErrors((prev) => ({
                          ...prev,
                          description: "",
                        }));
                      }
                    }}
                  />

                  <div className="flex justify-between items-center mt-1">
                    <div>
                      {errors.description && (
                        <p className="text-red-500 text-xs">
                          {errors.description}
                        </p>
                      )}
                    </div>

                    <p className="text-xs text-gray-500">
                      {description.length}/250
                    </p>
                  </div>
                </div>
              </div>

            </div>

            <div className="flex justify-between items-center mt-4">
              <button
                onClick={() => setShowAllModal(true)}
                className="bg-green-800 hover:bg-green-900 text-white px-6 py-2.5 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-2"
              >
                {t("viewAllButton")}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!fromDate || !leaveReasonId || isApplying}
                className="bg-green-800 hover:bg-green-900 text-white px-6 py-2.5 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-2"
              >
                {isApplying ? t("submitting") : t("applyButton")}
              </button>
            </div>

          </div>
        </div>
      </div>
      {/* Leave Details Popover / Modal */}
      {selectedLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden animate-fadeIn">
            <div className="flex justify-between items-center bg-green-800 text-white px-6 py-4">
              <h3 className="font-semibold text-lg">{t("leaveDetailsTitle")}</h3>
              <button
                onClick={() => setSelectedLeave(null)}
                className="text-white hover:text-gray-200 font-bold text-xl"
              >
                &times;
              </button>
            </div>

            <div className="p-6 space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-semibold text-gray-900">{t("durationLabel")} </span>
                {moment(selectedLeave.leave_from).format("DD MMM YYYY")} {t("to")}{" "}
                {moment(selectedLeave.leave_to).format("DD MMM YYYY")}
              </div>
              <div>
                <span className="font-semibold text-gray-900">{t("reasonLabel")} </span>
                {leaveReasons?.find(r => r.id === selectedLeave.leave_reason_id)?.leave_reason || t("notApplicable")}
              </div>
              <div>
                <span className="font-semibold text-gray-900 block mb-1">{t("commentLabel")}: </span>
                <p className="bg-gray-50 p-3 rounded border border-gray-100 text-gray-600">
                  {selectedLeave.description}
                </p>
              </div>
              {/* <div>
                <span className="font-semibold text-gray-900">Status: </span> 
                <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 capitalize">
                  {selectedLeave.status || 'Applied'}
                </span>
              </div> */}
            </div>

            <div className="bg-gray-50 px-6 py-3 flex justify-end gap-3 border-t border-gray-100">
              <button
                onClick={() => setSelectedLeave(null)}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 transition-colors text-sm font-medium"
              >
                {t("closeButton")}
              </button>

              {/* Only show Delete button if the leave starts in the future */}
              {/* {new Date(selectedLeave.leave_from) > new Date().setHours(0, 0, 0, 0) && (
                <button
                  onClick={() => handleDeleteLeave(selectedLeave.id)}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors shadow-sm"
                >
                  Delete Leave
                </button>
              )} */}
            </div>
          </div>
        </div>
      )}
      {/* View All Leaves Modal */}
      {showAllModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[80vh] animate-fadeIn">
            <div className="flex justify-between items-center bg-green-800 text-white px-6 py-4">
              <h3 className="font-semibold text-lg">{t("allAppliedLeavesTitle")}</h3>
              <button
                onClick={() => setShowAllModal(false)}
                className="text-white hover:text-gray-200 font-bold text-xl"
              >
                &times;
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-3 flex-1">
              {appliedLeaves && appliedLeaves.length > 0 ? (
                appliedLeaves
                  .slice()
                  .sort((a, b) => new Date(b.leave_from) - new Date(a.leave_from)) // Sort newest first
                  .map((leave) => {
                    const isFuture = new Date(leave.leave_from) > new Date().setHours(0, 0, 0, 0);
                    return (
                      <div key={leave.id} className="border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:border-gray-300 transition-colors bg-white shadow-sm">
                        <div className="space-y-1 text-sm">
                          <div className="font-semibold text-gray-900 flex items-center gap-2">
                            <span>{moment(leave.leave_from).format("DD MMM YYYY")}</span>
                            <span className="text-gray-400">{t("to")}</span>
                            <span>{moment(leave.leave_to).format("DD MMM YYYY")}</span>
                          </div>
                          <div className="text-gray-600">
                            <span className="font-medium text-gray-700">{t("reasonLabel")} </span>
                            {leave?.leave_reason?.leave_reason || t("notApplicable")}
                          </div>
                          <p className="text-gray-500 text-xs bg-gray-50 p-2 rounded mt-1 border border-gray-100">
                            {leave.description}
                          </p>
                          {/* <span
                            className={`inline-flex items-center rounded-xl px-2.5 py-1 text-xs font-medium inset-ring inset-ring-green-600/20 ${
                              leave.status === "pending"
                                ? "bg-yellow-50 text-yellow-800"
                                : leave.status === "approved" || leave.status === "active"
                                ? "bg-green-50 text-green-700"
                                : "bg-red-50 text-red-700"
                            }`}
                          >
                            {leave.status ? leave.status.charAt(0).toUpperCase() + leave.status.slice(1).toLowerCase() : "Applied"}
                        </span> */}
                        </div>

                        {isFuture && (
                          <button
                            onClick={() => handleDeleteLeave(leave.id)}
                            className="bg-red-50 hover:bg-red-100 text-red-600 px-3 py-1.5 rounded text-xs font-medium transition-colors border border-red-200 shrink-0"
                          >
                            {t("deleteButton")}
                          </button>
                        )}
                      </div>
                    );
                  })
              ) : (
                <p className="text-center text-gray-500 py-8">{t("noRecordsFound")}</p>
              )}
            </div>

            <div className="bg-gray-50 px-6 py-3 flex justify-end border-t border-gray-100">
              <button
                onClick={() => setShowAllModal(false)}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 transition-colors text-sm font-medium"
              >
                {t("closeButton")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}